import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Globe, Activity, Clock, Mail, Sparkles, X, Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

function ToneSelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-zinc-950 border border-zinc-800 px-2 py-1 font-mono text-[11px] text-zinc-300"
      data-testid="followup-tone-select"
    >
      <option value="warm">Warm</option>
      <option value="direct">Direct</option>
      <option value="curious">Curious</option>
    </select>
  );
}

function FollowupComposer({ recordingId, session, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | drafting | review | sending | sent
  const [tone, setTone] = useState("warm");
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [toEmail, setToEmail] = useState("");
  const [error, setError] = useState("");

  const draftIt = async () => {
    setPhase("drafting"); setError("");
    try {
      const r = await api.post(`/walkthroughs/${recordingId}/draft-followup`, {
        session_id: session.session_id,
        tone,
      });
      setDraft({ subject: r.data.subject, body: r.data.body });
      setPhase("review");
    } catch (e) {
      setPhase("idle");
      setError(e?.response?.data?.detail || "DARA couldn't draft right now.");
    }
  };

  const send = async () => {
    if (!toEmail) { setError("Add the recipient's email first."); return; }
    setPhase("sending"); setError("");
    try {
      const r = await api.post(`/walkthroughs/${recordingId}/send-followup`, {
        session_id: session.session_id,
        to_email: toEmail,
        subject: draft.subject,
        body: draft.body,
      });
      setPhase("sent");
      toast.success(r.data.delivered ? "Follow-up sent." : "Logged (Resend not configured — check log driver).");
    } catch (e) {
      setPhase("review");
      setError(e?.response?.data?.detail || "Couldn't send.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-[9998] flex items-center justify-center p-4" data-testid="followup-composer">
      <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full">
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FFB000]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FFB000]">// DARA-DRAFTED FOLLOW-UP</span>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </header>
        <div className="p-5 space-y-4">
          <div className="border border-zinc-800 p-3 bg-black/40">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">VIEWER SESSION</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 font-mono text-[11px]">
              <div><span className="text-zinc-500">Region:</span> <span className="text-white">{session.region}</span></div>
              <div><span className="text-zinc-500">Watched:</span> <span className="text-[#FFB000]">{Math.round(session.completion_pct)}%</span></div>
              <div><span className="text-zinc-500">Re-watches:</span> <span className="text-[#00E676]">{session.rewatch_count_for_ip}</span></div>
              <div><span className="text-zinc-500">Last seen:</span> <span className="text-white">{fmtDate(session.last_seen_at).split(",")[0]}</span></div>
            </div>
          </div>

          {phase === "idle" && (
            <div className="flex items-end gap-3">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">TONE</div>
                <ToneSelect value={tone} onChange={setTone} />
              </div>
              <button
                onClick={draftIt}
                data-testid="followup-draft-btn"
                className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:bg-[#E59E00] flex items-center gap-2"
              >
                <Sparkles className="w-3.5 h-3.5" /> DRAFT WITH DARA
              </button>
            </div>
          )}

          {phase === "drafting" && (
            <div className="flex items-center gap-2 text-zinc-400 font-mono text-[11px]">
              <Loader2 className="w-4 h-4 animate-spin text-[#FFB000]" /> DARA is composing…
            </div>
          )}

          {(phase === "review" || phase === "sending" || phase === "sent") && (
            <>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">TO</div>
                <input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="prospect@company.com"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white"
                  data-testid="followup-to-input"
                  disabled={phase === "sent"}
                />
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SUBJECT</div>
                <input
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white"
                  data-testid="followup-subject-input"
                  disabled={phase === "sent"}
                />
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">BODY</div>
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={9}
                  className="w-full bg-black border border-zinc-800 px-3 py-2 text-[13px] text-white leading-relaxed font-sans"
                  data-testid="followup-body-textarea"
                  disabled={phase === "sent"}
                />
              </div>
              {phase === "sent" ? (
                <div className="flex items-center gap-2 text-[#00E676] font-mono text-[11px]">
                  <CheckCircle2 className="w-4 h-4" /> Email logged. Use COPY to paste into your client if Resend isn&apos;t configured.
                </div>
              ) : (
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={draftIt} className="border border-zinc-800 px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900" data-testid="followup-redraft-btn">RE-DRAFT</button>
                  <button
                    onClick={send}
                    disabled={phase === "sending"}
                    data-testid="followup-send-btn"
                    className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:bg-[#E59E00] disabled:opacity-50 flex items-center gap-2"
                  >
                    {phase === "sending" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    SEND FOLLOW-UP
                  </button>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 text-[#FF3B30] font-mono text-[11px]">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WalkthroughAnalytics({ recordingId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composeFor, setComposeFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/walkthroughs/${recordingId}/analytics`);
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Couldn't load analytics" });
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recordingId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 font-mono text-[11px]" data-testid="analytics-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FFB000]" /> Loading analytics…
      </div>
    );
  }
  if (!data || data.error) {
    return <div className="text-zinc-500 font-mono text-[11px]">{data?.error || "No analytics yet."}</div>;
  }
  const { totals = {}, regions = [], sessions = [] } = data;
  return (
    <div className="space-y-4" data-testid="analytics-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          ["SESSIONS", totals.sessions, "text-[#FFB000]"],
          ["UNIQUE IPS", totals.unique_ips, "text-white"],
          ["COMPLETED", totals.completed, "text-[#00E676]"],
          ["AVG WATCH %", `${totals.avg_completion_pct}%`, "text-[#FFB000]"],
        ].map(([label, val, cls]) => (
          <div key={label} className="border border-zinc-900 p-3 bg-black/40">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className={`font-heading font-black text-2xl mt-1 ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      {regions.length > 0 && (
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> REGIONS
          </div>
          <div className="flex flex-wrap gap-1.5">
            {regions.map((r) => (
              <span key={r.region} className="border border-zinc-800 px-2 py-1 font-mono text-[10px] text-zinc-300">
                {r.region} <span className="text-[#FFB000] ml-1">×{r.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
          <Activity className="w-3 h-3" /> WATCH SESSIONS · NEWEST FIRST
        </div>
        {sessions.length === 0 && (
          <div className="text-zinc-600 font-mono text-[11px] py-3" data-testid="analytics-empty-sessions">
            No watch sessions recorded yet — share the link with a prospect.
          </div>
        )}
        {sessions.length > 0 && (
          <div className="border border-zinc-900 max-h-[280px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/60 sticky top-0">
                <tr>
                  {["Region", "Watch %", "Re-watches", "Started", "Last seen", ""].map((h) => (
                    <th key={h} className="text-left font-mono text-[9px] uppercase tracking-widest text-zinc-500 px-3 py-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-t border-zinc-900 hover:bg-zinc-900/30" data-testid={`analytics-row-${s.session_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{s.region}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      <span className={s.completed ? "text-[#00E676]" : s.completion_pct >= 50 ? "text-[#FFB000]" : "text-zinc-500"}>
                        {Math.round(s.completion_pct)}%{s.completed && " ✓"}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">×{s.rewatch_count_for_ip}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-500"><Clock className="w-3 h-3 inline mr-1" />{fmtDate(s.started_at)}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{fmtDate(s.last_seen_at)}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setComposeFor(s)}
                        data-testid={`analytics-followup-${s.session_id}`}
                        className="border border-[#FFB000]/40 text-[#FFB000] px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FFB000]/10 flex items-center gap-1"
                      >
                        <Mail className="w-3 h-3" /> FOLLOW UP
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {composeFor && (
        <FollowupComposer
          recordingId={recordingId}
          session={composeFor}
          onClose={() => { setComposeFor(null); load(); }}
        />
      )}
    </div>
  );
}
